"use client"

import { useEffect, useState, type ComponentType } from "react"
import TaskSimulatorStatic, { type SimulatorContent } from "./TaskSimulatorStatic"

type TaskSimulatorProps = {
  content: SimulatorContent
}

const ANIMATION_FALLBACK_DELAY_MS = 8000

export default function TaskSimulator({ content }: TaskSimulatorProps) {
  const [AnimatedTaskSimulator, setAnimatedTaskSimulator] =
    useState<ComponentType<TaskSimulatorProps> | null>(null)

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return

    let isCancelled = false
    let hasStartedLoading = false

    const loadAnimatedSimulator = () => {
      if (hasStartedLoading) return
      hasStartedLoading = true
      void import("./TaskSimulatorAnimated").then((module) => {
        if (isCancelled) return
        setAnimatedTaskSimulator(() => module.default)
      })
    }

    const interactionEvents = ["pointerdown", "keydown", "scroll"] as const
    interactionEvents.forEach((eventName) => {
      window.addEventListener(eventName, loadAnimatedSimulator, {
        once: true,
        passive: true,
      })
    })

    const timeoutId = window.setTimeout(
      loadAnimatedSimulator,
      ANIMATION_FALLBACK_DELAY_MS,
    )

    return () => {
      isCancelled = true
      window.clearTimeout(timeoutId)
      interactionEvents.forEach((eventName) => {
        window.removeEventListener(eventName, loadAnimatedSimulator)
      })
    }
  }, [])

  if (AnimatedTaskSimulator) {
    return <AnimatedTaskSimulator content={content} />
  }

  return <TaskSimulatorStatic content={content} />
}
